import { useTranslation } from '@/app/providers';
import { Card } from '@/shared/components/card';
import { Skeleton } from '@/shared/components/skeleton';
import { cn } from '@/shared/lib/cn';

const CONSENT_ROW_COUNT = 4;

function FieldSkeleton() {
  return (
    <div className="space-y-1.5">
      <Skeleton className="h-4 w-20 rounded-md" />
      <Skeleton className="h-12 w-full rounded-xs" />
    </div>
  );
}

/**
 * Layout-shaped loading state for the public data-collection form (`/rfrm`).
 * Mirrors the two-column card: promo panel, fields, consents, submit.
 */
export function PublicDcfFormSkeleton() {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-h-screen items-start justify-center bg-content-bg px-4 py-8 sm:items-center sm:py-10"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('publicDataCollectionForm.loading')}
    >
      <Card
        className="w-full max-w-5xl overflow-hidden border-primary/40 shadow-sm"
        padding="none"
      >
        <div className="md:grid md:grid-cols-[minmax(240px,38%)_minmax(0,1fr)]">
          <div className="flex min-h-56 flex-col gap-4 bg-primary/5 px-6 py-7 sm:min-h-64 sm:px-8 sm:py-8">
            <Skeleton variant="circular" className="h-16 w-16" />
            <Skeleton className="h-6 w-24 rounded-md" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full rounded-md" />
              <Skeleton className="h-4 w-11/12 rounded-md" />
              <Skeleton className="h-4 w-4/5 rounded-md" />
            </div>
          </div>

          <div className="bg-card px-5 py-6 sm:px-8">
            <div className="mb-4 flex justify-end">
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>

            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-16 rounded-md" />
                    <Skeleton className="h-3 w-16 rounded-md" />
                  </div>
                  <Skeleton className="h-12 w-full rounded-xs" />
                </div>
                <FieldSkeleton />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-border pb-2">
                  <Skeleton className="h-4 w-36 rounded-md" />
                  <Skeleton className="h-4 w-20 rounded-md" />
                </div>
                {Array.from({ length: CONSENT_ROW_COUNT }, (_, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
                    <Skeleton
                      className={cn(
                        'h-3.5 rounded-md',
                        index === 3 ? 'w-24' : 'w-52 max-w-[85%]',
                      )}
                    />
                  </div>
                ))}
              </div>

              <Skeleton className="h-12 w-full rounded-lg" />
              <div className="flex justify-end">
                <Skeleton className="h-3 w-28 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </Card>
      <span className="sr-only">{t('publicDataCollectionForm.loading')}</span>
    </div>
  );
}
